/**
 * ReportUploadForm — unified report upload form used by both
 *   • Stock Detail → Reports tab (company is locked to the active page)
 *   • Reports admin page /reports (company must be picked)
 *
 * Feature parity across the two locations:
 *   • Type: Annual | Interim | Other
 *   • Year: current year … current-5
 *   • Month: January … December
 *   • Period is derived as "Month Year" (e.g. "March 2025").
 *   • Shows a preview dialog listing Company / Type / Period / File
 *     before sending the upload request.
 *   • Drag-and-drop + click-to-pick file input (PDF only).
 *   • After a successful upload, calls onUploaded() so the parent can
 *     refresh its list without a full page reload.
 *
 * This component is admin-only — parents MUST gate rendering with
 * isAdmin. It does no internal admin check, so keep the gating at the
 * parent level consistent with the rest of the app.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiGet, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Upload, Loader2, ChevronsUpDown, Check, FileText, Sparkles,
} from "lucide-react";

const REPORT_TYPES = [
  { value: "annual", label: "Annual" },
  { value: "interim", label: "Interim" },
  { value: "other", label: "Other" },
];

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const currentYear = () => new Date().getFullYear();
const YEARS = (() => {
  const y = currentYear();
  // 2026 → y-5 range; we keep 6 years of coverage as requested.
  return [y, y - 1, y - 2, y - 3, y - 4, y - 5].map(String);
})();

const DEFAULT_MONTH = MONTHS[new Date().getMonth()];

// -----------------------------------------------------------------------
// Embedded company picker — uses /companies endpoint. Purposefully
// lives in-file (vs extending StockSearchDropdown) because we need a
// company_code, not a cse_symbol, and we want to match on ticker OR name
// in a single searchable command.
// -----------------------------------------------------------------------
const CompanyPicker = ({ value, onChange, testId = "upload-company-picker" }) => {
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    apiGet("/companies").then(setCompanies).catch(() => setCompanies([]));
  }, []);

  const selected = useMemo(
    () => companies.find((c) => c.company_code === value) || null,
    [companies, value],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid={testId}
        >
          {selected ? (
            <span className="truncate">
              <span className="font-mono text-xs opacity-70 mr-2">{selected.company_code}</span>
              {selected.company_name}
            </span>
          ) : (
            <span className="text-muted-foreground">Search ticker or company…</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command
          filter={(val, search) => {
            if (!search) return 1;
            return val.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Type ticker or company…" data-testid={`${testId}-input`} />
          <CommandList>
            <CommandEmpty>No companies match.</CommandEmpty>
            <CommandGroup>
              {companies.map((c) => {
                const key = `${c.company_code} ${c.company_name}`;
                return (
                  <CommandItem
                    key={c.company_code}
                    value={key}
                    onSelect={() => {
                      onChange(c.company_code, c);
                      setOpen(false);
                    }}
                    data-testid={`${testId}-option-${c.company_code}`}
                    className="gap-2"
                  >
                    <Check
                      className={`h-4 w-4 ${c.company_code === value ? "opacity-100" : "opacity-0"}`}
                    />
                    <span className="font-mono text-xs text-muted-foreground w-14 shrink-0">{c.company_code}</span>
                    <span className="truncate">{c.company_name}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// -----------------------------------------------------------------------
// Upload preview dialog
// -----------------------------------------------------------------------
const PreviewDialog = ({ open, payload, busy, progress = 0, onConfirm, onCancel }) => {
  if (!payload) return null;
  const { companyName, companyCode, reportType, period, file } = payload;
  const typeLabel = REPORT_TYPES.find((t) => t.value === reportType)?.label || reportType;
  const fileSizeKb = file?.size ? Math.round(file.size / 1024) : 0;
  const fileSizeMb = file?.size ? (file.size / (1024 * 1024)).toFixed(1) : null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onCancel(); }}>
      <DialogContent data-testid="report-upload-preview-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2" data-testid="upload-preview-title">
            <FileText className="h-5 w-5 text-primary" /> Confirm Upload
          </DialogTitle>
          <DialogDescription>
            Please confirm the details below before uploading. The PDF will be stored
            and Claude will analyse it automatically once you click Confirm &amp; Upload.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border bg-muted/30 divide-y text-sm">
          <Row label="Company" value={companyName ? `${companyName} (${companyCode})` : companyCode} testId="preview-company" />
          <Row label="Type" value={`${typeLabel} report`} testId="preview-type" />
          <Row label="Period" value={period} testId="preview-period" />
          <Row
            label="File"
            value={
              file?.name
                ? `${file.name}${fileSizeMb ? ` · ${fileSizeMb} MB` : (fileSizeKb ? ` · ${fileSizeKb} KB` : "")}`
                : null
            }
            mono
            testId="preview-file"
          />
        </div>
        {/* Indeterminate shimmer — shown only while the upload is in
            flight. The earlier per-byte percentage was swapped out
            because driving React state from `XHRUpload.onprogress`
            caused a render storm that starved the network thread on
            fast uplinks (see doUpload() history comment). Shimmer
            conveys motion without any per-byte state updates. */}
        {busy && (
          <div className="space-y-1.5" data-testid="upload-progress-wrap">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-foreground inline-flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                Uploading{fileSizeMb ? ` ${fileSizeMb} MB` : ""}… please keep this window open.
                We'll auto-retry if the network drops.
              </span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
              <div
                className="h-full w-1/3 bg-[hsl(var(--primary))] animate-[shimmer_1.2s_ease-in-out_infinite]"
                style={{
                  animation: "cseShimmer 1.2s ease-in-out infinite",
                }}
                data-testid="upload-progress-bar"
              />
            </div>
            <style>{`
              @keyframes cseShimmer {
                0% { transform: translateX(-100%); }
                100% { transform: translateX(300%); }
              }
            `}</style>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="preview-cancel-btn">
            {busy ? "Cancel upload" : "Cancel"}
          </Button>
          <Button onClick={onConfirm} disabled={busy} data-testid="preview-upload-btn" className="gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {busy ? "Uploading…" : "Confirm & Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, value, mono = false, testId }) => (
  <div className="px-3 py-2 flex items-baseline gap-3">
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground w-20 shrink-0">{label}</div>
    <div className={`flex-1 ${mono ? "font-mono text-xs break-all" : ""}`} data-testid={testId}>
      {value || <span className="text-muted-foreground/70">—</span>}
    </div>
  </div>
);

// -----------------------------------------------------------------------
// Main form
// -----------------------------------------------------------------------
// Persistent form state — stored OUTSIDE React state so parent re-renders
// (e.g. 10 s polling ticks on the reports list) can never reset the user's
// Type / Year / Month / Company selections. The refs are seeded once at
// module load; components mirror them into local useState for controlled
// inputs and write back to the ref whenever a selection changes.
const _formStateRef = {
  companyCode: "",
  companyName: "",
  reportType: "annual",
  year: String(currentYear()),
  month: DEFAULT_MONTH,
};

const _ReportUploadFormInner = ({
  companyCode: fixedCompanyCode = null,   // Stock Detail pre-fills; admin page is empty
  companyName: fixedCompanyName = null,
  onUploaded,
}) => {
  // Seed local state from the persistent ref so navigating away and back
  // restores the last selections. `fixedCompanyCode` (Stock Detail page)
  // always overrides because the company is implicit there.
  const [companyCode, setCompanyCodeState] = useState(fixedCompanyCode || _formStateRef.companyCode || "");
  const [companyName, setCompanyNameState] = useState(fixedCompanyName || _formStateRef.companyName || "");
  const [reportType, setReportTypeState] = useState(_formStateRef.reportType);
  const [year, setYearState] = useState(_formStateRef.year);
  const [month, setMonthState] = useState(_formStateRef.month);
  const [file, setFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  // % progress for the upload itself (file transfer to the server).
  // Stays at 0 until the user clicks "Confirm & Upload".
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef();
  // AbortController for the in-flight fetch upload (replaces the
  // previous XHR reference — see doUpload() for the rationale).
  const abortRef = useRef(null);

  // Keep the latest onUploaded callback in a ref so the memoised form
  // always invokes the most recent parent callback, even though our
  // `React.memo` comparator below deliberately ignores `onUploaded`
  // identity (otherwise the form would unmount on every parent render
  // and reset its dropdowns).
  const onUploadedRef = useRef(onUploaded);
  useEffect(() => { onUploadedRef.current = onUploaded; }, [onUploaded]);

  // Wrap the setters so every change also writes to the persistent ref.
  const setCompanyCode = (v) => { _formStateRef.companyCode = v || ""; setCompanyCodeState(v); };
  const setCompanyName = (v) => { _formStateRef.companyName = v || ""; setCompanyNameState(v); };
  const setReportType = (v) => { _formStateRef.reportType = v; setReportTypeState(v); };
  const setYear = (v) => { _formStateRef.year = v; setYearState(v); };
  const setMonth = (v) => { _formStateRef.month = v; setMonthState(v); };

  // Keep local company fields in sync if parent changes them (e.g. switching tabs)
  useEffect(() => {
    if (fixedCompanyCode && fixedCompanyCode !== companyCode) {
      setCompanyCodeState(fixedCompanyCode);
      setCompanyNameState(fixedCompanyName || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedCompanyCode, fixedCompanyName]);

  const period = `${month} ${year}`;

  const openPreview = (selectedFile) => {
    const f = selectedFile || file;
    if (!f) {
      toast.error("Choose a PDF first");
      return;
    }
    if (!companyCode) {
      toast.error("Pick a company first");
      return;
    }
    setFile(f);
    setPreviewOpen(true);
  };

  const doUpload = async () => {
    if (!file || !companyCode) return;
    setUploading(true);
    setUploadProgress(0);

    const baseUrl = `${API_BASE}/reports/${companyCode}/upload?report_type=${encodeURIComponent(reportType)}&report_period=${encodeURIComponent(period)}`;
    const tok = localStorage.getItem("cse_auth_token") || "";
    const sizeMb = (file.size / (1024 * 1024)).toFixed(1);

    // -----------------------------------------------------------------
    // Upload a single attempt using fetch + FormData + AbortController.
    //
    // HISTORY:
    //   • v1 used XMLHttpRequest to surface `upload.onprogress` events
    //     for a per-byte progress bar. That caused a React render storm
    //     on fast uplinks (Starlink 19.5 MB/s), starving the network
    //     thread. Swapped for plain fetch().
    //   • v2 (current): plain fetch + 300 s AbortController timeout
    //     + 3-attempt retry ladder. Worked for most uploads but we saw
    //     the Kubernetes ingress / preview proxy occasionally swallow
    //     a POST request — DevTools shows "Initial connection: 5 min"
    //     and the backend never receives the body. Waiting 5 minutes
    //     before retrying is unacceptable UX for a 7 MB file.
    //
    // v3 (this rev) — fail fast, retry fresh:
    //   • 90 s timeout per attempt (a 50 MB upload on a 1 MB/s line is
    //     < 60 s; 90 s leaves slack for slow networks without making
    //     users wait 5 min for a stuck edge connection).
    //   • Each retry gets a fresh ?_attempt=N&_ts=… query so the
    //     proxy can't deduplicate / reuse stuck state.
    //   • Each attempt carries an X-Request-Id the server logs on
    //     receipt, so if a future upload hangs we can prove whether
    //     the body made it to FastAPI or died at the edge.
    //   • A watchdog toast at 20 s tells the user "still uploading,
    //     this can take a moment on slow links" so they don't panic.
    // -----------------------------------------------------------------
    const makeRequestId = () => {
      try {
        return (crypto && crypto.randomUUID && crypto.randomUUID())
          || `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      } catch {
        return `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      }
    };

    const ATTEMPT_TIMEOUT_MS = 90_000;

    const uploadOnce = async (attemptNum) => {
      if (attemptNum > 1) {
        toast.info(`Retry ${attemptNum} of 3 — the previous attempt stalled.`, { duration: 3500 });
      }
      const requestId = makeRequestId();
      // Cache-buster query params force a brand-new edge connection
      // if the previous one was wedged at a proxy.
      const url = `${baseUrl}&_attempt=${attemptNum}&_ts=${Date.now()}`;
      const controller = new AbortController();
      abortRef.current = controller;
      let timedOut = false;
      const timeoutId = setTimeout(() => {
        timedOut = true;
        try { controller.abort(); } catch { /* ignore */ }
      }, ATTEMPT_TIMEOUT_MS);

      // Watchdog toast — if the attempt is still running 20 s in,
      // reassure the user. Important for 20-50 MB PDFs on slow links.
      const watchdogId = setTimeout(() => {
        toast.info("Still uploading… hold tight.", { duration: 2500, id: "upload-watchdog" });
      }, 20_000);

      const startedAt = performance.now();
      const fd = new FormData();
      fd.append("file", file);

      // eslint-disable-next-line no-console
      console.info(
        "[upload] start attempt=%d file=%s size=%sMB reqId=%s",
        attemptNum, file.name, sizeMb, requestId,
      );
      let res;
      try {
        res = await fetch(url, {
          method: "POST",
          body: fd,
          headers: {
            ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
            "X-Request-Id": requestId,
            // Cache-control hints — some edge proxies try to cache
            // matching request signatures; make it unambiguously
            // uncacheable.
            "Cache-Control": "no-cache, no-store, must-revalidate",
          },
          signal: controller.signal,
          // `keepalive` would let the request survive page unload; we
          // deliberately DO NOT set it here because Chrome caps
          // keepalive bodies at 64 KB which would silently truncate
          // any real PDF upload.
        });
      } catch (e) {
        clearTimeout(timeoutId);
        clearTimeout(watchdogId);
        abortRef.current = null;
        if (e.name === "AbortError") {
          if (timedOut) {
            // eslint-disable-next-line no-console
            console.warn("[upload] TIMEOUT attempt=%d reqId=%s after %dms", attemptNum, requestId, ATTEMPT_TIMEOUT_MS);
            const err = new Error(
              `Upload stalled after ${ATTEMPT_TIMEOUT_MS / 1000}s — the network edge likely dropped the connection. Retrying…`,
            );
            err.transient = true; throw err;
          }
          const err = new Error("Upload cancelled");
          err.aborted = true; err.transient = false; throw err;
        }
        const err = new Error(
          "Connection lost during upload. Please check your internet connection and try again.",
        );
        err.transient = true; throw err;
      }
      clearTimeout(timeoutId);
      clearTimeout(watchdogId);
      abortRef.current = null;
      const elapsed = performance.now() - startedAt;
      // eslint-disable-next-line no-console
      console.info(
        "[upload] done attempt=%d status=%d elapsed=%dms size=%sMB speed=%sMB/s reqId=%s",
        attemptNum, res.status, Math.round(elapsed), sizeMb,
        (parseFloat(sizeMb) / (elapsed / 1000)).toFixed(2), requestId,
      );

      if (res.status >= 200 && res.status < 300) {
        try { return await res.json(); }
        catch { return {}; }
      }
      if (res.status === 413) {
        let detail = "File too large. Maximum size is 50 MB. Try a compressed PDF.";
        try { const j = await res.json(); if (j.detail) detail = j.detail; } catch { /* ignore */ }
        const err = new Error(detail); err.transient = false; throw err;
      }
      if (res.status === 401 || res.status === 403) {
        const err = new Error("You must be signed in as admin to upload reports.");
        err.transient = false; throw err;
      }
      // 502 / 503 / 504 / 524 are classic edge / upstream transient errors
      // (Cloudflare 524 = upstream timeout). Retry those.
      let detail = `Upload failed (HTTP ${res.status})`;
      try { const j = await res.json(); if (j.detail) detail = j.detail; } catch { /* ignore */ }
      const err = new Error(detail);
      err.transient = res.status >= 500 || res.status === 408 || res.status === 429;
      throw err;
    };

    // Retry ladder — tight on delay because each attempt already
    // waits up to 90 s on its own. Total worst-case wall-clock:
    //   attempt 1 (90 s) + 2 s + attempt 2 (90 s) + 5 s + attempt 3 (90 s)
    //   ≈ 4 minutes, down from 15 min with the old 300 s-per-attempt ladder.
    const delays = [0, 2000, 5000];
    let lastError = null;
    let created = null;
    for (let attempt = 1; attempt <= 3; attempt += 1) {
      const delay = delays[attempt - 1];
      if (delay > 0) {
        await new Promise((r) => setTimeout(r, delay));
      }
      try {
        created = await uploadOnce(attempt);
        break;
      } catch (e) {
        lastError = e;
        if (!e.transient || e.aborted) break;
      }
    }

    if (!created) {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 600);
      if (lastError && !lastError.aborted) {
        toast.error(lastError.message || "Upload failed");
      }
      return;
    }

    setUploadProgress(100);
    toast.success(`Report uploaded — ${reportType} · ${period}. Claude is analysing now.`);
    setFile(null);
    setPreviewOpen(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (onUploadedRef.current) onUploadedRef.current(created);
    setUploading(false);
    setTimeout(() => setUploadProgress(0), 600);
  };

  // Allow the user to cancel an in-flight upload (especially handy for
  // 50 MB PDFs they realised had the wrong file selected).
  const cancelUpload = () => {
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* ignore */ }
      abortRef.current = null;
    }
    setUploading(false);
    setUploadProgress(0);
    setPreviewOpen(false);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onFilePicked = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files are accepted");
      return;
    }
    openPreview(f);
  };

  const showCompanyPicker = !fixedCompanyCode;

  return (
    <div className="space-y-3" data-testid="report-upload-form">
      <div
        className={`grid grid-cols-1 ${showCompanyPicker ? "md:grid-cols-4" : "md:grid-cols-3"} gap-3`}
      >
        {showCompanyPicker && (
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1 block">
              Company
            </label>
            <CompanyPicker
              value={companyCode}
              onChange={(code, c) => {
                setCompanyCode(code);
                setCompanyName(c?.company_name || "");
              }}
            />
          </div>
        )}
        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1 block">
            Type
          </label>
          <Select value={reportType} onValueChange={setReportType}>
            <SelectTrigger data-testid="report-upload-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REPORT_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1 block">
            Year
          </label>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger data-testid="report-upload-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1 block">
            Month
          </label>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger data-testid="report-upload-month">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <label
        htmlFor="report-upload-dropzone-input"
        onDragOver={(e) => {
          e.preventDefault();
          e.currentTarget.classList.add("border-[hsl(var(--primary))]");
        }}
        onDragLeave={(e) => { e.currentTarget.classList.remove("border-[hsl(var(--primary))]"); }}
        onDrop={(e) => {
          e.preventDefault();
          e.currentTarget.classList.remove("border-[hsl(var(--primary))]");
          const f = e.dataTransfer.files?.[0];
          onFilePicked(f);
        }}
        className="block rounded-lg border-2 border-dashed border-border bg-muted/20 px-6 py-8 text-center cursor-pointer hover:bg-muted/40 transition-colors"
        data-testid="report-upload-dropzone"
      >
        <input
          id="report-upload-dropzone-input"
          ref={fileInputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={(e) => onFilePicked(e.target.files?.[0])}
          data-testid="report-upload-input"
        />
        <div className="flex flex-col items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" />
          <p className="text-sm font-medium">
            Drop a PDF here or click to choose
          </p>
          <p className="text-xs text-muted-foreground">
            We'll preview the details below before uploading. Claude will analyse the report
            in the background.
          </p>
          {companyCode && (
            <p className="text-[11px] text-muted-foreground">
              Ready to upload for{" "}
              <span className="font-medium text-foreground">{companyName || companyCode}</span>
              {" · "}
              <span className="font-medium text-foreground capitalize">{reportType}</span>
              {" · "}
              <span className="font-medium text-foreground">{period}</span>
            </p>
          )}
        </div>
      </label>

      <PreviewDialog
        open={previewOpen}
        busy={uploading}
        progress={uploadProgress}
        payload={
          previewOpen
            ? { companyCode, companyName, reportType, period, file }
            : null
        }
        onCancel={cancelUpload}
        onConfirm={doUpload}
      />
    </div>
  );
};

// React.memo with a tight comparator — the form only re-renders when the
// two parent-controlled props (companyCode / companyName) actually
// change. The `onUploaded` callback identity is intentionally IGNORED
// so the parent can pass `() => load()` inline without forcing the
// form to unmount on every parent render (which was causing the form
// dropdowns to visibly "disappear" every time polling fired).
export const ReportUploadForm = React.memo(
  _ReportUploadFormInner,
  (prev, next) =>
    prev.companyCode === next.companyCode && prev.companyName === next.companyName,
);
ReportUploadForm.displayName = "ReportUploadForm";

export default ReportUploadForm;
